// 立替払い（ForFinance::Payment）。キーは API（DB のカラム名を lowerCamel にしたもの）に合わせる

export type ForFinancePaymentStatus =
  | "unpaid"
  | "individual_transfer_completed"
  | "total_transfer_completed"
  | "canceled"
  | "reserved"
  | "no_transfer_sales_only"
  | "transfer_requested"
  | "transfer_completed"

export type ForFinancePaymentMethod = "undecided" | "gmo_advance" | "smooth_direct"

// SmootherOPE と同じ 16 項目
export type UpdateForFinancePaymentRequest = {
  userId: string | null
  userName: string | null
  storeId: string | null
  transferApplicationId: number | null
  customerReferralFeeRateWithTax: number | null
  isSalesOffset: boolean | null
  advancedRemittanceAmount: number | null
  reimbursedAt: string | null
  applicationId: string | null
  status: ForFinancePaymentStatus | null
  method: ForFinancePaymentMethod | null
  memo: string | null
  totalInitialCost: number | null
  advancedPrincipleAmount: number | null
  rentCost: number | null
  customerReferralFeeWithTax: number | null
}

export type ForFinancePaymentDetail = Omit<
  UpdateForFinancePaymentRequest,
  "userId" | "storeId" | "applicationId" | "customerReferralFeeRateWithTax"
> & {
  id: number
  // DB は文字列カラムだが数値で返る場合もあるため両方受ける
  userId: string | number | null
  storeId: string | number | null
  // 店舗は SmootherOPE と同じく名前で表示する。店舗が見つからなければ null
  storeName: string | null
  applicationId: string | number | null
  customerReferralFeeRateWithTax: number | string | null
  payoutApplicationId: number | null
  createdAt: string
  updatedAt: string
}

export type ForFinancePaymentListQuery = {
  search: string
  dateFrom: string
  dateTo: string
  order: ForFinancePaymentSortKey
  direction: "asc" | "desc"
  page: number
}

export type ForFinancePaymentSortKey = keyof Omit<
  ForFinancePaymentDetail,
  "storeName" | "payoutApplicationId" | "updatedAt"
>

// 表示ラベルは SmootherOPE（smooth_api の enums.for_finance_payment）と同じにする
export const FOR_FINANCE_PAYMENT_STATUS_LABELS: Record<ForFinancePaymentStatus, string> = {
  unpaid: "未払い",
  individual_transfer_completed: "個別振込済",
  total_transfer_completed: "総合振込済",
  canceled: "キャンセル",
  reserved: "保留",
  no_transfer_sales_only: "送金無し_売上計上のみ",
  transfer_requested: "送金依頼中",
  transfer_completed: "送金完了",
}

export const FOR_FINANCE_PAYMENT_METHOD_LABELS: Record<ForFinancePaymentMethod, string> = {
  undecided: "未定",
  gmo_advance: "GMO立替",
  smooth_direct: "SMOOTH直払い",
}

export const statusLabel = (v: string | null | undefined) =>
  v ? (FOR_FINANCE_PAYMENT_STATUS_LABELS[v as ForFinancePaymentStatus] ?? v) : "—"

export const methodLabel = (v: string | null | undefined) =>
  v ? (FOR_FINANCE_PAYMENT_METHOD_LABELS[v as ForFinancePaymentMethod] ?? v) : "—"

// 店舗名。店舗が見つからないときは ID を出す
export const storeLabel = (p: Pick<ForFinancePaymentDetail, "storeId" | "storeName">) =>
  p.storeName ?? (p.storeId != null && p.storeId !== "" ? `ID: ${p.storeId}` : "—")

export const salesOffsetLabel = (v: boolean | null | undefined) =>
  v == null ? "—" : v ? "有" : "無"

// 一覧の列（SmootherOPE と同じ順・同じ表記）。key は並べ替えの order にも使う。
// 表記は SmootherOPE の一覧の列名に合わせる（訳語のない列は英語のまま）
export const FOR_FINANCE_PAYMENT_COLUMNS: { key: ForFinancePaymentSortKey; header: string }[] = [
  { key: "id", header: "Id" },
  { key: "userId", header: "Userid" },
  { key: "userName", header: "Username" },
  { key: "storeId", header: "Store" },
  { key: "transferApplicationId", header: "立替送金申請ID" },
  { key: "customerReferralFeeRateWithTax", header: "顧客紹介手数料率" },
  { key: "isSalesOffset", header: "売上相殺" },
  { key: "advancedRemittanceAmount", header: "送金額" },
  { key: "reimbursedAt", header: "送金予定日" },
  { key: "applicationId", header: "Applicationid" },
  { key: "status", header: "Status" },
  { key: "method", header: "支払い方法" },
  { key: "memo", header: "Memo" },
  { key: "totalInitialCost", header: "初期費用総額" },
  { key: "advancedPrincipleAmount", header: "立替元本" },
  { key: "rentCost", header: "家賃" },
  { key: "customerReferralFeeWithTax", header: "送客売上税込" },
]

// 立替送金申請のページ（既存）。該当の申請に絞り込んで開く
export const transferApplicationHref = (transferApplicationId: number) =>
  `/admin/transfer-applications?transferApplicationId=${transferApplicationId}`
